import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiSave, FiSearch, FiUser } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { useAdminAccess } from '../utilities/adminAccess';
import { champFields } from '../utilities/tournamentsApi';
import { extractPlayersPage, getPlayerPhotoUrl, playerDisplayName } from '../utilities/playersApi';
import {
  BLACKLIST_COMMENT_MAX,
  blacklistComment,
  blacklistDisplayName,
  blacklistPlayerId,
  extractBlacklistPlayer,
  formatBlacklistDate,
  trimBlacklistComment,
} from '../utilities/blacklistApi';
import CustomModal from '../components/CustomModal';
import StadiumCrumbs from '../components/StadiumCrumbs';
import defaultPlayerImage from '../images/defaultAvatar.png';
import '../styles/createMatchModal.css';
import '../styles/stadiumsManagement.css';
import '../styles/disqualificationsManagement.css';
import '../styles/blackList.css';

export default function ManageBlackListPlayer() {
  const { playerId } = useParams();
  const isNew = !playerId;
  const dispatch = useDispatch();
  const history = useHistory();
  const { t, lang } = useI18n();
  const { champId, loading: champsLoading } = useChampionships();
  const { canWrite } = useAdminAccess('tournaments');

  const [player, setPlayer] = useState(null);
  const [comment, setComment] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [pickerPlayers, setPickerPlayers] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerError, setPickerError] = useState('');
  const pickerTimer = useRef(null);
  const pickerRequestRef = useRef(0);

  useEffect(() => {
    if (!champId && champsLoading) return undefined;
    initPage();
    return undefined;
  }, [playerId, champId, champsLoading]);

  const initPage = async () => {
    setMessage({ text: '', type: '' });
    setErrors({});
    if (isNew) {
      setPlayer(null);
      setComment('');
      setLoading(false);
      return;
    }
    if (!champId) {
      setLoading(false);
      setMessage({ text: t('blLoadError'), type: 'error' });
      return;
    }
    setLoading(true);
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminBlackListPlayer, {
        id: Number(playerId),
        ...champFields(champId),
      });
      const next = extractBlacklistPlayer(response.data);
      if (!next) {
        setPlayer(null);
        setComment('');
        setMessage({ text: t('blNotFound'), type: 'error' });
      } else {
        setPlayer(next);
        setComment(blacklistComment(next));
      }
    } catch (error) {
      setPlayer(null);
      setComment('');
      setMessage({ text: getApiErrorMessage(error, t('blLoadError')), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadPickerPlayers = async (search) => {
    const requestId = ++pickerRequestRef.current;
    setPickerLoading(true);
    setPickerError('');
    try {
      const body = {
        ...champFields(champId),
        limit: 20,
        offset: 0,
      };
      if (search && search.trim()) body.search = search.trim();
      const response = await authorizedPost(dispatch, API_URLS.getAdminPlayers, body);
      if (requestId !== pickerRequestRef.current) return;
      setPickerPlayers(extractPlayersPage(response.data).items);
    } catch (error) {
      if (requestId !== pickerRequestRef.current) return;
      setPickerPlayers([]);
      setPickerError(getApiErrorMessage(error, t('blNoPlayers')));
    } finally {
      if (requestId === pickerRequestRef.current) setPickerLoading(false);
    }
  };

  useEffect(() => {
    if (!pickerOpen) return undefined;
    if (pickerTimer.current) clearTimeout(pickerTimer.current);
    const delay = pickerQuery ? 350 : 0;
    pickerTimer.current = setTimeout(() => {
      loadPickerPlayers(pickerQuery);
    }, delay);
    return () => {
      if (pickerTimer.current) clearTimeout(pickerTimer.current);
    };
  }, [pickerOpen, pickerQuery, champId]);

  const closePicker = () => {
    setPickerOpen(false);
    setPickerQuery('');
    setPickerPlayers([]);
    setPickerError('');
  };

  const validate = () => {
    const nextErrors = {};
    if (isNew && !blacklistPlayerId(player)) nextErrors.player = t('blNeedPlayer');
    const nextComment = trimBlacklistComment(comment);
    if (!nextComment) nextErrors.comment = t('blNeedComment');
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setMessage({ text: '', type: '' });
    const nextComment = trimBlacklistComment(comment);
    try {
      if (isNew) {
        const response = await authorizedPost(dispatch, API_URLS.addAdminBlackList, {
          id: blacklistPlayerId(player),
          ...champFields(champId),
          comment: nextComment,
        });
        const created = extractBlacklistPlayer(response.data);
        const createdId = blacklistPlayerId(created) || blacklistPlayerId(player);
        if (createdId) history.replace(`/black_list/${createdId}`);
        else history.push('/black_list');
        return;
      }
      const response = await authorizedPost(dispatch, API_URLS.changeAdminBlackList, {
        id: Number(playerId),
        ...champFields(champId),
        comment: nextComment,
      });
      const updated = extractBlacklistPlayer(response.data);
      if (updated) {
        setPlayer(updated);
        setComment(blacklistComment(updated));
      }
      setMessage({ text: t('blSaved'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('blSaveError')), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await authorizedPost(dispatch, API_URLS.deleteAdminBlackList, {
        id: Number(playerId),
        ...champFields(champId),
      });
      history.push('/black_list');
    } catch (error) {
      setDeleteVisible(false);
      setMessage({ text: getApiErrorMessage(error, t('blRemoveError')), type: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const title = isNew ? t('blNew') : (blacklistDisplayName(player) || t('blEdit'));

  if (loading) {
    return (
      <div className="layoutWrapper st-layout">
        <div className="matchCenterWrapper st-page">
          <div className="matchCenterHeader">
            <div className="st-header">
              <StadiumCrumbs
                items={[
                  { label: t('navBlackList'), to: '/black_list' },
                  { label: isNew ? t('blNew') : t('blEdit') },
                ]}
              />
            </div>
          </div>
          <div className="st-loading">
            <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
            <p>{t('blLoading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs
              items={[
                { label: t('navBlackList'), to: '/black_list' },
                { label: title },
              ]}
            />
          </div>
        </div>

        <div className="st-scroll">
          <div className="st-content">
            {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

            <div className="st-block">
              <div className="st-form-grid">
                <div className="st-field st-field-wide">
                  <label>{t('blPlayer')} {isNew ? <span className="st-required">*</span> : null}</label>
                  {isNew ? (
                    <>
                      <button
                        type="button"
                        className={`st-input dq-picker-btn ${errors.player ? 'error' : ''}`.trim()}
                        onClick={() => setPickerOpen(true)}
                      >
                        {player ? (
                          <span className="dq-picker-current">
                            <img
                              src={getPlayerPhotoUrl(player.image) || defaultPlayerImage}
                              alt=""
                              onError={(event) => { event.currentTarget.src = defaultPlayerImage; }}
                            />
                            <span>
                              <strong>{blacklistDisplayName(player)}</strong>
                            </span>
                          </span>
                        ) : (
                          <span className="dq-picker-placeholder">
                            <FiUser /> {t('blPickPlayer')}
                          </span>
                        )}
                      </button>
                      {errors.player ? <span className="st-field-error">{errors.player}</span> : null}
                    </>
                  ) : (
                    <div className="bl-card-head">
                      <img
                        src={getPlayerPhotoUrl(player?.image) || defaultPlayerImage}
                        alt=""
                        onError={(event) => { event.currentTarget.src = defaultPlayerImage; }}
                      />
                      <div>
                        <strong>{blacklistDisplayName(player) || '—'}</strong>
                        <span>{t('blDate')}: {formatBlacklistDate(player?.blacklistedTimestamp, lang) || t('blNotSet')}</span>
                        <span>{t('blAddedBy')}: {player?.blacklistedBy_name || '—'}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="st-field st-field-wide">
                  <label>{t('blReason')} <span className="st-required">*</span></label>
                  <textarea
                    className={`st-textarea ${errors.comment ? 'error' : ''}`}
                    value={comment}
                    maxLength={BLACKLIST_COMMENT_MAX}
                    onChange={(event) => {
                      setComment(event.target.value);
                      if (errors.comment) setErrors((prev) => ({ ...prev, comment: '' }));
                    }}
                    placeholder={t('blReasonPh')}
                  />
                  {errors.comment ? <span className="st-field-error">{errors.comment}</span> : null}
                </div>
              </div>
            </div>
          </div>
        </div>

        {canWrite ? (
          <div className={`st-actions ${!isNew ? 'st-actions-split' : ''}`}>
            {!isNew ? (
              <button
                type="button"
                className="st-btn st-btn-danger"
                disabled={saving || deleting || !player}
                onClick={() => setDeleteVisible(true)}
              >
                {t('delete')}
              </button>
            ) : <span />}
            <button
              type="button"
              className="st-btn st-btn-primary"
              disabled={saving || (!isNew && !player)}
              onClick={handleSave}
            >
              <FiSave /> {saving ? t('blLoading') : (isNew ? t('blAddConfirm') : t('stdSaveChanges'))}
            </button>
          </div>
        ) : null}
      </div>

      <CustomModal
        styles={{ width: 480, height: 'auto', paddingLeft: 24, paddingRight: 24, paddingTop: 20, paddingBottom: 20 }}
        visible={pickerOpen}
        closeModal={closePicker}
      >
        <div className="dq-picker-modal">
          <div className="customModalHeader">{t('blPickPlayer')}</div>
          <div className="st-search">
            <FiSearch className="st-search-icon" />
            <input
              type="text"
              className="st-search-input"
              placeholder={t('blSearchPh')}
              value={pickerQuery}
              autoFocus
              onChange={(event) => setPickerQuery(event.target.value)}
            />
            {pickerQuery ? (
              <button type="button" className="st-search-clear" onClick={() => setPickerQuery('')}>
                ×
              </button>
            ) : null}
          </div>
          {pickerError ? <div className="st-field-error">{pickerError}</div> : null}
          {pickerLoading ? (
            <div className="dq-picker-loading">
              <Loader type="Oval" color="#3ad1a0" height={36} width={36} />
              <p>{t('ttLoadingPlayers')}</p>
            </div>
          ) : (
            <div className="dq-picker-list">
              {pickerPlayers.length ? pickerPlayers.map((item) => {
                const id = blacklistPlayerId(item);
                const already = Number(item.blacklisted) === 1;
                return (
                  <button
                    type="button"
                    key={id}
                    className="dq-picker-item"
                    disabled={!id || already}
                    onClick={() => {
                      setPlayer(item);
                      setErrors((prev) => ({ ...prev, player: '' }));
                      closePicker();
                    }}
                  >
                    <img
                      src={getPlayerPhotoUrl(item.image) || defaultPlayerImage}
                      alt=""
                      onError={(event) => { event.currentTarget.src = defaultPlayerImage; }}
                    />
                    <span>
                      <strong>{playerDisplayName(item) || `#${id}`}</strong>
                      <em>{already ? t('blAlreadyListed') : (item.championship_name || item.championat_name || t('ttNoTeam'))}</em>
                    </span>
                  </button>
                );
              }) : (
                <div className="dq-picker-empty">
                  <FiUser /> {t('blNoPlayers')}
                </div>
              )}
            </div>
          )}
        </div>
      </CustomModal>

      <CustomModal
        styles={{ width: 420, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
        visible={deleteVisible}
        closeModal={() => !deleting && setDeleteVisible(false)}
      >
        <div className="st-delete-modal">
          <div className="customModalHeader">{t('blDeleteTitle')}</div>
          <p>{t('blDeleteConfirm')}</p>
          <div className="st-delete-actions">
            <button
              type="button"
              className="btn-modal btn-secondary-modal"
              disabled={deleting}
              onClick={() => setDeleteVisible(false)}
            >
              {t('cancel')}
            </button>
            <button
              type="button"
              className="btn-modal btn-danger-modal"
              disabled={deleting}
              onClick={handleDelete}
            >
              {deleting ? t('blLoading') : t('delete')}
            </button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
